/**
 * Safety Reports API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  SafetyInspection,
  ToolboxTalk,
  SafetyObservation,
  SafetyStats,
  SafetyMetrics,
  InspectionListParams,
  InspectionListResponse,
  ToolboxTalkListParams,
  ToolboxTalkListResponse,
  ObservationListParams,
  ObservationListResponse,
  CreateInspectionRequest,
  UpdateInspectionRequest,
  CreateToolboxTalkRequest,
  UpdateToolboxTalkRequest,
  RecordAttendanceRequest,
  CreateObservationRequest,
  UpdateObservationRequest,
  ResolveObservationRequest,
} from '../types/safety';

/**
 * Safety Reports API Endpoints
 */
export const safetyApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // STATS & METRICS
    // ============================================================================

    getSafetyStats: builder.query<ApiResponse<SafetyStats>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/safety/stats`,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: 'SafetyStats', id: projectId },
      ],
    }),

    getSafetyMetrics: builder.query<
      ApiResponse<SafetyMetrics>,
      { projectId: string; dateFrom?: string; dateTo?: string }
    >({
      query: ({ projectId, dateFrom, dateTo }) => ({
        url: `/projects/${projectId}/safety/metrics`,
        params: { dateFrom, dateTo },
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: 'SafetyStats', id: `metrics-${projectId}` },
      ],
    }),

    // ============================================================================
    // INSPECTIONS CRUD
    // ============================================================================

    getInspections: builder.query<
      ApiResponse<InspectionListResponse>,
      { projectId: string; params?: InspectionListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/safety/inspections`,
        params,
      }),
      providesTags: ['SafetyInspection'],
    }),

    getInspection: builder.query<
      ApiResponse<SafetyInspection>,
      { projectId: string; inspectionId: string }
    >({
      query: ({ projectId, inspectionId }) => ({
        url: `/projects/${projectId}/safety/inspections/${inspectionId}`,
      }),
      providesTags: (_result, _error, { inspectionId }) => [
        { type: 'SafetyInspection', id: inspectionId },
      ],
    }),

    createInspection: builder.mutation<
      ApiResponse<SafetyInspection>,
      { projectId: string; data: CreateInspectionRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/safety/inspections`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SafetyInspection', 'SafetyStats'],
    }),

    updateInspection: builder.mutation<
      ApiResponse<SafetyInspection>,
      { projectId: string; inspectionId: string; data: UpdateInspectionRequest }
    >({
      query: ({ projectId, inspectionId, data }) => ({
        url: `/projects/${projectId}/safety/inspections/${inspectionId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { inspectionId }) => [
        'SafetyInspection',
        { type: 'SafetyInspection', id: inspectionId },
        'SafetyStats',
      ],
    }),

    deleteInspection: builder.mutation<
      ApiResponse<void>,
      { projectId: string; inspectionId: string }
    >({
      query: ({ projectId, inspectionId }) => ({
        url: `/projects/${projectId}/safety/inspections/${inspectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SafetyInspection', 'SafetyStats'],
    }),

    // ============================================================================
    // INSPECTION WORKFLOW
    // ============================================================================

    completeInspection: builder.mutation<
      ApiResponse<SafetyInspection>,
      { projectId: string; inspectionId: string }
    >({
      query: ({ projectId, inspectionId }) => ({
        url: `/projects/${projectId}/safety/inspections/${inspectionId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { inspectionId }) => [
        'SafetyInspection',
        { type: 'SafetyInspection', id: inspectionId },
        'SafetyStats',
      ],
    }),

    reviewInspection: builder.mutation<
      ApiResponse<SafetyInspection>,
      { projectId: string; inspectionId: string }
    >({
      query: ({ projectId, inspectionId }) => ({
        url: `/projects/${projectId}/safety/inspections/${inspectionId}/review`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { inspectionId }) => [
        'SafetyInspection',
        { type: 'SafetyInspection', id: inspectionId },
      ],
    }),

    closeInspection: builder.mutation<
      ApiResponse<SafetyInspection>,
      { projectId: string; inspectionId: string }
    >({
      query: ({ projectId, inspectionId }) => ({
        url: `/projects/${projectId}/safety/inspections/${inspectionId}/close`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { inspectionId }) => [
        'SafetyInspection',
        { type: 'SafetyInspection', id: inspectionId },
        'SafetyStats',
      ],
    }),

    // ============================================================================
    // TOOLBOX TALKS CRUD
    // ============================================================================

    getToolboxTalks: builder.query<
      ApiResponse<ToolboxTalkListResponse>,
      { projectId: string; params?: ToolboxTalkListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/safety/toolbox-talks`,
        params,
      }),
      providesTags: ['ToolboxTalk'],
    }),

    getToolboxTalk: builder.query<
      ApiResponse<ToolboxTalk>,
      { projectId: string; talkId: string }
    >({
      query: ({ projectId, talkId }) => ({
        url: `/projects/${projectId}/safety/toolbox-talks/${talkId}`,
      }),
      providesTags: (_result, _error, { talkId }) => [{ type: 'ToolboxTalk', id: talkId }],
    }),

    createToolboxTalk: builder.mutation<
      ApiResponse<ToolboxTalk>,
      { projectId: string; data: CreateToolboxTalkRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/safety/toolbox-talks`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ToolboxTalk', 'SafetyStats'],
    }),

    updateToolboxTalk: builder.mutation<
      ApiResponse<ToolboxTalk>,
      { projectId: string; talkId: string; data: UpdateToolboxTalkRequest }
    >({
      query: ({ projectId, talkId, data }) => ({
        url: `/projects/${projectId}/safety/toolbox-talks/${talkId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { talkId }) => [
        'ToolboxTalk',
        { type: 'ToolboxTalk', id: talkId },
        'SafetyStats',
      ],
    }),

    deleteToolboxTalk: builder.mutation<
      ApiResponse<void>,
      { projectId: string; talkId: string }
    >({
      query: ({ projectId, talkId }) => ({
        url: `/projects/${projectId}/safety/toolbox-talks/${talkId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ToolboxTalk', 'SafetyStats'],
    }),

    completeToolboxTalk: builder.mutation<
      ApiResponse<ToolboxTalk>,
      { projectId: string; talkId: string }
    >({
      query: ({ projectId, talkId }) => ({
        url: `/projects/${projectId}/safety/toolbox-talks/${talkId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { talkId }) => [
        'ToolboxTalk',
        { type: 'ToolboxTalk', id: talkId },
        'SafetyStats',
      ],
    }),

    recordAttendance: builder.mutation<
      ApiResponse<ToolboxTalk>,
      { projectId: string; talkId: string; data: RecordAttendanceRequest }
    >({
      query: ({ projectId, talkId, data }) => ({
        url: `/projects/${projectId}/safety/toolbox-talks/${talkId}/attendance`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { talkId }) => [
        'ToolboxTalk',
        { type: 'ToolboxTalk', id: talkId },
      ],
    }),

    // ============================================================================
    // OBSERVATIONS CRUD
    // ============================================================================

    getObservations: builder.query<
      ApiResponse<ObservationListResponse>,
      { projectId: string; params?: ObservationListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/safety/observations`,
        params,
      }),
      providesTags: ['SafetyObservation'],
    }),

    getObservation: builder.query<
      ApiResponse<SafetyObservation>,
      { projectId: string; observationId: string }
    >({
      query: ({ projectId, observationId }) => ({
        url: `/projects/${projectId}/safety/observations/${observationId}`,
      }),
      providesTags: (_result, _error, { observationId }) => [
        { type: 'SafetyObservation', id: observationId },
      ],
    }),

    createObservation: builder.mutation<
      ApiResponse<SafetyObservation>,
      { projectId: string; data: CreateObservationRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/safety/observations`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SafetyObservation', 'SafetyStats'],
    }),

    updateObservation: builder.mutation<
      ApiResponse<SafetyObservation>,
      { projectId: string; observationId: string; data: UpdateObservationRequest }
    >({
      query: ({ projectId, observationId, data }) => ({
        url: `/projects/${projectId}/safety/observations/${observationId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { observationId }) => [
        'SafetyObservation',
        { type: 'SafetyObservation', id: observationId },
        'SafetyStats',
      ],
    }),

    deleteObservation: builder.mutation<
      ApiResponse<void>,
      { projectId: string; observationId: string }
    >({
      query: ({ projectId, observationId }) => ({
        url: `/projects/${projectId}/safety/observations/${observationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SafetyObservation', 'SafetyStats'],
    }),

    resolveObservation: builder.mutation<
      ApiResponse<SafetyObservation>,
      { projectId: string; observationId: string; data: ResolveObservationRequest }
    >({
      query: ({ projectId, observationId, data }) => ({
        url: `/projects/${projectId}/safety/observations/${observationId}/resolve`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { observationId }) => [
        'SafetyObservation',
        { type: 'SafetyObservation', id: observationId },
        'SafetyStats',
      ],
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  // Stats & Metrics
  useGetSafetyStatsQuery,
  useGetSafetyMetricsQuery,
  // Inspections
  useGetInspectionsQuery,
  useGetInspectionQuery,
  useCreateInspectionMutation,
  useUpdateInspectionMutation,
  useDeleteInspectionMutation,
  useCompleteInspectionMutation,
  useReviewInspectionMutation,
  useCloseInspectionMutation,
  // Toolbox Talks
  useGetToolboxTalksQuery,
  useGetToolboxTalkQuery,
  useCreateToolboxTalkMutation,
  useUpdateToolboxTalkMutation,
  useDeleteToolboxTalkMutation,
  useCompleteToolboxTalkMutation,
  useRecordAttendanceMutation,
  // Observations
  useGetObservationsQuery,
  useGetObservationQuery,
  useCreateObservationMutation,
  useUpdateObservationMutation,
  useDeleteObservationMutation,
  useResolveObservationMutation,
} = safetyApi;

/**
 * Re-export types for convenience
 */
export type {
  InspectionListParams,
  ToolboxTalkListParams,
  ObservationListParams,
  SafetyStats,
  SafetyMetrics,
} from '../types/safety';
